import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

import { prefetchCase, useCase } from "@/api";
import { sendWarnings } from "@/lib/mailRequest";

import styles from "./SendWarnings.module.css";

/** When the Vorgang could not be read: said calmly, nothing is locked (review final, 3). */
export const NOT_COMPARED = "Abgleich mit der Mail nicht geladen.";

/**
 * What the mails asked for and the offer does not hold, in the question
 * before „Angebot raus" (UX-Gutachten 4): a release given before the mail
 * checks — 2026-0004, Pressrost released, the mail asks for Barfuß and RAL
 * 7016 — went out without a word. Said calmly, never locked: the person may
 * send what the mail did not ask for. Nothing is rendered without a warning.
 *
 * The list carries `id`, so the yes can be described by it
 * (`aria-describedby`), and is named by its **visible** label
 * (`aria-labelledby`), not by an `aria-label`: Chrome takes a referenced
 * element's `aria-label` for the description, and the yes then read „Aus
 * der Mail" instead of the sentences (review final, 1, measured).
 */
export function SendWarnings({ id, warnings }: { id: string; warnings: readonly string[] }) {
  if (warnings.length === 0) return null;
  const labelId = `${id}-label`;
  return (
    <div className={styles.block}>
      <span id={labelId} className="label">
        Aus der Mail
      </span>
      <ul id={id} className={styles.list} aria-labelledby={labelId}>
        {warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Reads the Vorgang ahead for `CaseSendWarnings` — `StageControl`'s
 * `onSendIntent`, on hover or focus of „Angebot raus" (review final, 3).
 */
export function useReadAhead(caseId: string): () => void {
  const client = useQueryClient();
  return useCallback(() => prefetchCase(client, caseId), [client, caseId]);
}

/**
 * The same, for a place that knows only the Vorgang's id (the register's
 * row, the record's head): reads `GET /cases/{id}` — once it is rendered,
 * i.e. once the question is open, or earlier where the trigger was hovered
 * or focused (`usePrefetchCase`). Nothing while the answer is on its way; a
 * calm line where it failed (`NOT_COMPARED`, review final, 3).
 *
 * `onShown` tells whether something stands under `id`, so the yes is
 * described only by an element that exists (review final, 4).
 */
export function CaseSendWarnings({
  caseId,
  id,
  onShown,
}: {
  caseId: string;
  id: string;
  onShown?: (shown: boolean) => void;
}) {
  const query = useCase(caseId);
  const detail = query.data;
  const warnings = detail ? sendWarnings(detail.mailRequest, detail.gratingType) : [];
  const failed = !detail && query.isError;
  const shown = warnings.length > 0 || failed;
  useEffect(() => {
    onShown?.(shown);
    return () => onShown?.(false);
  }, [shown, onShown]);
  if (failed) {
    return (
      <p id={id} className={styles.list}>
        {NOT_COMPARED}
      </p>
    );
  }
  return <SendWarnings id={id} warnings={warnings} />;
}
